import React, { useState, useEffect } from 'react';
import { Search, Filter, Download, List, Info, Terminal } from 'lucide-react';
import socketService from '../../services/socketService';

const PacketAnalyzer = ({ scenarioData }) => {
    const [packets, setPackets] = useState([]);
    const [filter, setFilter] = useState('');
    const [selectedPacket, setSelectedPacket] = useState(null);

    const fetchData = () => {
        // Fallback: fetch from OpenAEV if no scenario data
        fetch('http://localhost:3001/api/openaev/payloads')
            .then(res => res.json())
            .then(data => {
                const formattedPackets = data.map((p, index) => ({
                    no: index + 1,
                    time: new Date(p.payload_created_at).toISOString(),
                    source: p.network_traffic_ip_src || 'Unknown',
                    destination: p.network_traffic_ip_dst || 'Unknown',
                    protocol: p.network_traffic_protocol || 'TCP',
                    length: Math.floor(Math.random() * 1500),
                    info: p.payload_description || p.payload_name,
                    raw: p // Keep raw data for details pane
                }));
                setPackets(formattedPackets);
            })
            .catch(err => console.error(err));
    };

    useEffect(() => {
        // If scenarioData is provided, use it directly
        if (scenarioData && scenarioData.length > 0) {
            const formattedPackets = scenarioData.map((p, index) => ({
                no: index + 1,
                time: p.timestamp || new Date().toISOString(),
                source: p.source || 'Unknown',
                destination: p.dest || 'Unknown',
                protocol: p.protocol || 'TCP',
                length: p.size || Math.floor(Math.random() * 1500),
                info: p.info || `${p.protocol} packet`,
                raw: p
            }));
            setPackets(formattedPackets);
        } else {
            fetchData();

            // Socket Listener for real-time updates from OpenAEV
            const socket = socketService.connect();
            socket.on('network_event', (newEvent) => {
                console.log('PacketAnalyzer: New network event:', newEvent);
                fetchData(); // Refresh list on new events
            });

            return () => {
                socketService.disconnect();
            };
        }
    }, [scenarioData]);

    const filteredPackets = packets.filter(p =>
        (p.source || '').includes(filter) ||
        (p.destination || '').includes(filter) ||
        (p.protocol || '').includes(filter) ||
        (p.info || '').includes(filter)
    );

    return (
        <div className="bg-white text-black h-full flex flex-col font-sans text-xs border border-gray-300 shadow-xl overflow-hidden">
            {/* Toolbar */}
            <div className="bg-gray-100 p-1 border-b border-gray-300 flex gap-2 items-center">
                <div className="flex items-center bg-white border border-gray-300 rounded px-2 w-full max-w-md">
                    <Filter size={12} className="text-gray-500 mr-2" />
                    <input
                        type="text"
                        placeholder="Apply a display filter ... <Ctrl-/>"
                        className="w-full p-0.5 outline-none text-xs"
                        value={filter}
                        onChange={(e) => setFilter(e.target.value)}
                    />
                </div>
                <button className="p-1 hover:bg-gray-200 rounded" title="Search"><Search size={14} /></button>
                <button className="p-1 hover:bg-gray-200 rounded" title="Export"><Download size={14} /></button>
            </div>

            {/* Main Content Split View */}
            <div className="flex-1 flex flex-col overflow-hidden">
                {/* Packet List */}
                <div className="flex-1 overflow-auto border-b border-gray-300">
                    <table className="w-full border-collapse table-fixed">
                        <thead className="bg-gray-200 sticky top-0 z-10">
                            <tr className="text-gray-700">
                                <th className="border-r border-gray-300 px-2 py-1 text-left font-semibold w-12">No.</th>
                                <th className="border-r border-gray-300 px-2 py-1 text-left font-semibold w-32">Time</th>
                                <th className="border-r border-gray-300 px-2 py-1 text-left font-semibold w-32">Source</th>
                                <th className="border-r border-gray-300 px-2 py-1 text-left font-semibold w-32">Destination</th>
                                <th className="border-r border-gray-300 px-2 py-1 text-left font-semibold w-20">Protocol</th>
                                <th className="border-r border-gray-300 px-2 py-1 text-left font-semibold w-20">Length</th>
                                <th className="px-2 py-1 text-left font-semibold">Info</th>
                            </tr>
                        </thead>
                        <tbody className="font-mono">
                            {filteredPackets.map((packet) => (
                                <tr
                                    key={packet.no}
                                    onClick={() => setSelectedPacket(packet)}
                                    className={`
                                        cursor-pointer border-b border-gray-100
                                        ${selectedPacket?.no === packet.no ? 'bg-blue-600 text-white' : 'hover:bg-blue-100'}
                                        ${selectedPacket?.no !== packet.no && packet.protocol === 'TCP' ? 'bg-[#e7e6ff]' : ''}
                                        ${selectedPacket?.no !== packet.no && packet.protocol === 'UDP' ? 'bg-[#daeeff]' : ''}
                                        ${selectedPacket?.no !== packet.no && packet.protocol === 'HTTP' ? 'bg-[#e4ffc7]' : ''}
                                        ${selectedPacket?.no !== packet.no && packet.protocol === 'DNS' ? 'bg-[#ffebcd]' : ''}
                                    `}
                                >
                                    <td className="px-2 py-0.5 truncate">{packet.no}</td>
                                    <td className="px-2 py-0.5 truncate">{packet.time}</td>
                                    <td className="px-2 py-0.5 truncate">{packet.source}</td>
                                    <td className="px-2 py-0.5 truncate">{packet.destination}</td>
                                    <td className="px-2 py-0.5 truncate">{packet.protocol}</td>
                                    <td className="px-2 py-0.5 truncate">{packet.length}</td>
                                    <td className="px-2 py-0.5 truncate">{packet.info}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                {/* Packet Details Pane */}
                <div className="h-1/3 bg-white flex flex-col overflow-hidden">
                    <div className="bg-gray-100 px-2 py-1 border-b border-gray-300 flex items-center gap-2 text-gray-600 font-semibold">
                        <Info size={14} />
                        <span>Packet Details</span>
                    </div>
                    <div className="flex-1 overflow-auto p-2 font-mono text-xs">
                        {selectedPacket ? (
                            <div className="space-y-1">
                                <div className="text-blue-800 font-bold border-b border-gray-200 pb-1 mb-1">
                                    Frame {selectedPacket.no}: {selectedPacket.length} bytes on wire
                                </div>
                                <div className="pl-4">
                                    <div className="flex gap-2"><span className="text-gray-500 w-24">Arrival Time:</span> {selectedPacket.time}</div>
                                    <div className="flex gap-2"><span className="text-gray-500 w-24">Protocol:</span> {selectedPacket.protocol}</div>
                                    <div className="flex gap-2"><span className="text-gray-500 w-24">Source:</span> {selectedPacket.source}</div>
                                    <div className="flex gap-2"><span className="text-gray-500 w-24">Destination:</span> {selectedPacket.destination}</div>
                                </div>
                                <div className="text-green-800 font-bold border-b border-gray-200 pb-1 mt-3 mb-1">
                                    Payload Data (JSON)
                                </div>
                                <pre className="bg-gray-50 p-2 rounded border border-gray-200 overflow-x-auto">
                                    {JSON.stringify(selectedPacket.raw, null, 2)}
                                </pre>
                            </div>
                        ) : (
                            <div className="h-full flex flex-col items-center justify-center text-gray-400 italic">
                                <List size={32} className="mb-2 opacity-20" />
                                Select a packet to view details
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* Status Bar */}
            <div className="bg-gray-100 px-2 py-0.5 border-t border-gray-300 flex justify-between text-[10px] text-gray-500">
                <div className="flex gap-4">
                    <span>Packets: {packets.length}</span>
                    <span>Displayed: {filteredPackets.length}</span>
                </div>
                <div className="flex items-center gap-1">
                    <div className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></div>
                    <span>Live Capture Active</span>
                </div>
            </div>
        </div>
    );
};

export default PacketAnalyzer;